import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import PdfUploadRow from './PdfUploadRow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Doc-management panel rendered BELOW the user table on User Management.
 * Two sections, in this order (per Raisey's preferred surfacing):
 *
 *   1. Terms of Trade PDF             — upload/replace/remove the agreement.
 *   2. Quality Assurance Certifications — four optional cert slots.
 *
 * The Terms of Trade acceptance log used to live here as well — it now lives
 * on the consolidated **Audit Log** page (see `/audit-log`), alongside
 * password-change and prepay-credit lifecycle events.
 */
const GlobalDocsPanel = () => {
  const [termsMeta, setTermsMeta] = useState({ present: false });
  const [certs, setCerts] = useState([]);

  const fetchTermsMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/terms`);
      setTermsMeta(res.data || { present: false });
    } catch (e) {
      console.error('Error fetching T&Cs metadata:', e);
    }
  }, []);

  const fetchCerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/certifications`);
      setCerts(res.data?.items || []);
    } catch (e) {
      console.error('Error fetching certifications:', e);
    }
  }, []);

  useEffect(() => { fetchTermsMeta(); fetchCerts(); }, [fetchTermsMeta, fetchCerts]);

  return (
    <div className="space-y-6 mt-8" data-testid="global-docs-panel">
      <div className="bg-surface border border-border-color rounded-xl p-6" data-testid="terms-management-panel">
        <h2 className="text-xl font-bold font-heading text-gold mb-2">Terms of Trade PDF</h2>
        <p className="text-xs text-text-secondary mb-4">
          Stored in the database and attached to every submitted order email. Customers must tick the
          &quot;I agree&quot; checkbox before they can submit. Max 5&nbsp;MB.
        </p>
        <PdfUploadRow
          label="Terms of Trade"
          meta={termsMeta}
          viewHref={`${process.env.REACT_APP_BACKEND_URL}/api/terms/file`}
          uploadUrl={`${API}/terms`}
          deleteUrl={`${API}/terms`}
          onChanged={fetchTermsMeta}
          testIdPrefix="terms"
          confirmDelete={
            'Remove the current Terms of Trade PDF? Customers will see an error until you upload a new one.'
          }
        />
      </div>

      <div className="bg-surface border border-border-color rounded-xl p-6" data-testid="certifications-management-panel">
        <h2 className="text-xl font-bold font-heading text-gold mb-2">Quality Assurance Certifications</h2>
        <p className="text-xs text-text-secondary mb-4">
          Optional PDFs surfaced under <em>&quot;Raisey&apos;s Quality Assurance&quot;</em> in the sidebar
          for customers to download. Leave a slot empty to hide it. Max 5&nbsp;MB each.
        </p>
        {certs.map((c) => (
          <PdfUploadRow
            key={c.key}
            label={c.label}
            meta={c}
            viewHref={`${process.env.REACT_APP_BACKEND_URL}/api/certifications/${c.key}/file`}
            uploadUrl={`${API}/certifications/${c.key}`}
            onChanged={fetchCerts}
            testIdPrefix={`cert-${c.key}`}
            confirmDelete={
              `Remove the "${c.label}" PDF? It will be hidden from the customer sidebar until you upload a new one.`
            }
          />
        ))}
      </div>
    </div>
  );
};

export default GlobalDocsPanel;
